// src/lib/performance.js — agent performance metrics (Build #1: Activity pillar)
import { toGel } from './rankingScore.js'
// As-of spell readers. ./spells.js imports nothing, so this cannot form a cycle with
// statsModel.js (which imports from THIS file).
import { officeAt, officesCoveredAt, rateAt } from './spells.js'
//
// Pillars (planned): attendance (lib/attendance.js + StatsTab.computeAgentStats),
// activity (here), results (future — closed deals / earnings). For now this file
// owns the ACTIVITY pillar: confirmed-posted listings from the extension's
// `user_events` table. Only `published === true` rows count — failures are an app
// problem, not the agent's, so they're ignored entirely (no failed/​success-rate).

// Canonical join key between an agent and their `user_events`. The extension logs
// in as `${login_id}@sentavus.com`, so login_id is the real key; the contact
// `email` is only a fallback. ⚠️ This is the single source of truth for that
// mapping — ListingsTab imports it (don't re-implement, or the two drift and
// agents get double-counted / missed, the 2026 login_id bug).
export function agentEventKeys(a) {
  const keys = []
  const lid = (a.login_id || '').trim().toLowerCase()
  if (lid) keys.push(lid.includes('@') ? lid : `${lid}@sentavus.com`)
  const em = (a.email || '').trim().toLowerCase()
  if (em && !keys.includes(em)) keys.push(em)
  return keys
}

// Local-time YYYY-MM-DD for an event timestamp. Matches how the day-range fetch
// and the rest of the app bucket days (local midnight, not UTC).
function localDay(iso) {
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

// Activity for one agent over a set of pre-fetched events (already range-bounded).
// Returns counts of CONFIRMED-posted listings only.
//   published   — total posted in range
//   byPlatform  — { myhome, ss, korter }
//   byDate      — { 'YYYY-MM-DD': count } (posted per day)
//   bestDay     — { date, count } the single most-productive day ("most in a day")
//   activeDays  — distinct days with at least one post
export function computeActivity(events, agent) {
  const keys = new Set(agentEventKeys(agent))
  const byPlatform = { myhome: 0, ss: 0, korter: 0 }
  const byDate = {}
  let published = 0

  for (const e of events) {
    if (e.published !== true) continue
    const email = (e.user_email || '').trim().toLowerCase()
    if (!email || !keys.has(email)) continue
    published++
    if (e.platform && byPlatform[e.platform] != null) byPlatform[e.platform]++
    const day = localDay(e.created_at)
    byDate[day] = (byDate[day] || 0) + 1
  }

  let bestDay = { date: null, count: 0 }
  for (const [date, count] of Object.entries(byDate)) {
    if (count > bestDay.count) bestDay = { date, count }
  }

  return { published, byPlatform, byDate, bestDay, activeDays: Object.keys(byDate).length }
}

// The same result as computeActivity, from rows that are ALREADY counted per
// (user_email, day, platform) — see getPublishedDaily in db/index.js for why the Stats
// tab no longer downloads the raw events. Same shape out, so every consumer of
// computeActivity's result (perScheduledDay, bestDay, byDate for the trend charts) reads
// this without change. Keep the two in step: a field added here must be added there.
export function computeActivityFromDaily(rows, agent) {
  const keys = new Set(agentEventKeys(agent))
  const byPlatform = { myhome: 0, ss: 0, korter: 0 }
  const byDate = {}
  let published = 0

  for (const r of rows || []) {
    const email = (r.user_email || '').trim().toLowerCase()
    if (!email || !keys.has(email)) continue
    const n = Number(r.n) || 0
    if (n <= 0) continue
    published += n
    if (r.platform && byPlatform[r.platform] != null) byPlatform[r.platform] += n
    // `day` may arrive as a date string or a full timestamp — keep the calendar day.
    const day = String(r.day || '').slice(0, 10)
    if (!day) continue
    byDate[day] = (byDate[day] || 0) + n
  }

  let bestDay = { date: null, count: 0 }
  for (const [date, count] of Object.entries(byDate)) {
    if (count > bestDay.count) bestDay = { date, count }
  }

  return { published, byPlatform, byDate, bestDay, activeDays: Object.keys(byDate).length }
}

// Posted-per-scheduled-day — the fair rate metric (doesn't penalise part-timers /
// shorter schedules). scheduledDays comes from the attendance computation. Used
// for both posts (activity) and commission/value (results).
export function perScheduledDay(value, scheduledDays) {
  if (!scheduledDays) return null
  return +(value / scheduledDays).toFixed(1)
}

// Posted-per-HOUR-WORKED (owner ruling 2026-09-10: "instead of გამოქვეყნება/დღე we need hour").
//
// 🔴 THIS ALSO FIXES A REAL DEFECT, which is why it replaced the per-day column rather than
// joining it. `perScheduledDay` divides by ROSTERED days, and for anyone whose roster is short
// the divisor collapses while the numerator does not. Measured on the live rows that prompted
// this: irakli (a Manager) posted 466 listings in 90 days against ~12 rostered days, and the
// table read **39.6 per day** — arithmetically exact and describing nobody's day. The row above
// his read 331.3. Hours worked cannot drift from the work the way a roster can: the numerator
// and the denominator are then measured over the same time.
//
// ⚠️ Null when nothing was worked, NEVER zero. An agent with no recorded hours has no rate, and
// printing 0.0 would rank them below someone who posted nothing in a full week — the same
// unknown-is-not-zero rule the rest of this file follows.
export function perHourWorked(value, workedMins) {
  if (!workedMins) return null
  return +(value / (workedMins / 60)).toFixed(1)
}

// Results for one agent over pre-fetched, range-bounded deals. Deals join by
// agent_id (they're entered in this app, not from the extension). Money is summed
// raw; the score normalises commission-per-day elsewhere.
//   count           — number of closed deals
//   totalCommission — earned money (the score driver)
//   totalValue      — total transaction value
//   bestDeal        — { commission, date } biggest single commission
export function computeResults(deals, agent) {
  let count = 0, totalCommission = 0, totalValue = 0
  let bestDeal = { commission: 0, date: null }
  for (const d of deals) {
    if (d.agent_id !== agent.id) continue
    count++
    const c = Number(d.commission) || 0
    const v = Number(d.deal_value) || 0
    totalCommission += c
    totalValue += v
    if (c > bestDeal.commission) bestDeal = { commission: c, date: d.date }
  }
  return { count, totalCommission, totalValue, bestDeal }
}

// The date a viewing counts as a closed result: payment date → the 'closed' event
// → created_at, whichever we have. Used to bucket results into the stats range.
export function viewingCloseDate(v) {
  if (v.payment_date) return v.payment_date
  const ev = (v.events || []).find(e => e.new_status === 'closed' || e.kind === 'closed')
  if (ev?.event_date) return ev.event_date
  if (ev?.event_at)   return ev.event_at.slice(0, 10)
  return (v.created_at || '').slice(0, 10)
}

// One agent's income from a deal. links = [{ agent_id, commission_pct }] (the
// rates snapshotted at close); leadAgentId = who attended the meeting. Rules:
//   • 1 agent: income = commission × rate/100 (agency keeps the rest).
//   • 2 agents, HIGHER-paid attended (or no lead set), or EQUAL pay: EACH TAKES HALF THEIR
//     OWN RATE. 70% + 50% → 35% and 25%, agency keeps 40%.
//   • 2 agents, LOWER-paid attended: the pot is the lower rate, shared evenly — each gets
//     min%/2 (agency keeps 100−min%). Not attending costs the higher-paid agent the
//     difference, which is the only thing attendance still changes about the money.
//   • 3+ agents: NOT SPECIFIED. Falls back to each agent's own %, which can pay out more
//     than the gross (three at 50% = 150%) and drive company-retained negative. No such
//     transaction exists in either database — max is 2, measured 2026-08-29 — so rather than
//     invent a rule for a case that has never occurred, lib/statsModel.js computes retained
//     as a real subtraction and FLAGS `overAllocated` if this branch ever produces one.
// ⚠️ CORRECTED 2026-08-29, SECOND PASS. The owner's written rule and its worked example said
// the higher-paid agent takes max − min/2 (45% on a 70/50 deal); they then corrected it to
// "when two split commission they get half of their pay", i.e. 35%. The written example is
// therefore WRONG and the correction stands — do not restore 45% from reading the spec.
// Tests pinning all cases live in lib/statsModel.test.js; change this function and they fail.
export function agentDealIncome(commissionAmount, links, agentId, leadAgentId) {
  const comm = Number(commissionAmount) || 0
  const me = (links || []).find(l => l.agent_id === agentId)
  if (!me) return 0
  const officeIds = [...new Set((links || []).map(l => l.office_id).filter(Boolean))]
  const officeLinks = me.office_id ? links.filter(l => l.office_id === me.office_id) : links
  const declaredOfficeShare = Number(me.office_share_percent)
  const officeShare = Number.isFinite(declaredOfficeShare) && declaredOfficeShare > 0
    ? declaredOfficeShare / 100 : (officeIds.length > 1 ? 1 / officeIds.length : 1)
  const officeComm = comm * officeShare
  const myPct = Number(me.commission_pct) || 0
  const rates = officeLinks.map(l => Number(l.commission_pct) || 0)
  if (officeLinks.length <= 1) return Math.round(officeComm * myPct / 100)
  if (officeLinks.length === 2) {
    const max = Math.max(...rates), min = Math.min(...rates)
    const leadPct = leadAgentId ? Number(officeLinks.find(l => l.agent_id === leadAgentId)?.commission_pct) : null
    const lowAttended = leadPct != null && leadPct === min && max !== min
    // The LOWER-paid agent attended → the pot is the lower rate and the two share it evenly.
    // Unchanged from the owner's original statement of the rules.
    if (lowAttended) return Math.round(officeComm * (min / 2) / 100)
    // Otherwise EACH AGENT TAKES HALF THEIR OWN RATE (owner correction, 2026-08-29:
    // "when two split commission they get half of their pay"). This replaces the earlier
    // `max − min/2` for the higher-paid agent: on 70% + 50% they now take 35%, not 45%, and
    // the agency keeps 40% rather than 30%. Equal rates are unaffected — own/2 and min/2 are
    // the same number — which is why all 17 legacy co-broked deals are untouched by this and
    // only the 3 core deals with unequal pairs move.
    return Math.round(officeComm * (myPct / 2) / 100)
  }
  return Math.round(officeComm * myPct / 100)
}

// ─── The OTHER half of the money: deals closed in core ────────────────────────
// 🚨 THE SCHEDULER'S `viewings` TABLE IS FROZEN. Its newest close is 2026-06-22; real deals
// have closed into core (`deal` + `deal_participant`) ever since — 15 of them in the 30 days
// to 2026-08-29, while `viewings` had none. Every money figure computed from viewings alone
// has therefore read dead data, which is how an agent with 4 closed deals and $1,525 of
// commission showed "—" on his own stats page (found by the owner, 2026-08-29).
//
// This adapts a core deal into the SAME shape computeResultsFromViewings already consumes,
// so there is exactly one income rule in this app rather than a second one that drifts.
// Everything the split needs maps across:
//   • commission_amount is OUR share, already net of any co-broke give-away (core 0449) —
//     the number payroll divides, which is precisely what the legacy field meant.
//   • the credited agents are the distinct scheduler agents among the deal's participants,
//     the same set core credits `deal.closed` to (0690).
//   • role 'went' = attended = the scheduler's lead_agent_id, which the 2-agent branch needs.
//
// ⚠️ EVERY agent's rate is filled, not just the viewer's. computeResults' own fallback fills a
// missing rate for the SELF only and leaves others at 0 — correct for a legacy row (where a
// co-broker's snapshot exists), catastrophic here: min would be 0 on every co-broked core deal
// and the split would price it wrong.
// Since core 0797 the deal carries `agent_links` — the rates it was CLOSED under — and those
// win. The roster's current rate is only the fallback for deals closed before that column
// existed, which is exactly what this function did for every deal before 0797.
export function coreDealsToViewings(coreDeals = [], roster = []) {
  const rateOf = id => {
    const a = roster.find(x => x.id === id)
    return a && a.commission != null ? Number(a.commission) : 0
  }
  return coreDeals.map(d => {
    const ids = (d.agents || []).map(a => a.scheduler_agent_id).filter(Boolean)
    // THE RATE THE DEAL WAS CLOSED UNDER wins over the agent's rate today (core 0797).
    // The owner's rule is explicit — "the agent's commission % AT THE MOMENT OF CLOSING" —
    // and without the snapshot one edit to somebody's % re-prices every deal they ever
    // closed. null = the deal predates 0797; then, and only then, fall back to the roster,
    // which is what this did for every deal before the column existed.
    const frozen = new Map((d.agent_links || []).map(l => [l.agent_id, l]))
    return {
      id: `core:${d.deal_id}`,
      source: 'core',
      status: 'closed',
      // closed_at is a timestamptz; viewingCloseDate wants a yyyy-mm-dd day.
      // review 2026-09-23: the TBILISI day (permanent UTC+4). Slicing the UTC ISO put a deal
      // closed 00:00–03:59 Georgia time on the 1st into the PREVIOUS month's figures.
      payment_date: (() => {
        if (!d.closed_at) return null
        const ms = Date.parse(d.closed_at)
        return Number.isFinite(ms) ? new Date(ms + 4 * 3600000).toISOString().slice(0, 10)
          : (String(d.closed_at).slice(0, 10) || null)
      })(),
      commission_amount: d.commission_amount,
      commission_currency: d.commission_currency || 'GEL',
      agent_ids: ids,
      agent_links: ids.map(id => {
        const f = frozen.get(id)
        return { agent_id: id,
          commission_pct: f ? Number(f.commission_pct) : rateOf(id),
          ...(f?.office_id ? { office_id: f.office_id } : {}),
          ...(f?.office_share_percent != null ? { office_share_percent: f.office_share_percent } : {}) }
      }),
      lead_agent_id: d.lead_scheduler_agent_id || null,
      manager_links: d.manager_links || null,
      property_snapshot: { core_listing_id: d.listing_id || null, core_property_id: d.property_id || null },
      legacy_viewing_id: d.legacy_viewing_id || null,
    }
  })
}

// Legacy viewings + core deals as one list, with the overlap removed. A core deal closed on a
// meeting that mirrors a legacy viewing would otherwise be counted twice, once from each
// source — zero such rows exist today (178 mirrored meetings, none carrying a deal), which is
// exactly why this must be code and not a comment: nothing would fail loudly the day one
// appears, the money would simply double.
export function mergeDealSources(viewings = [], coreDeals = [], roster = []) {
  const core = coreDealsToViewings(coreDeals, roster)
  const superseded = new Set(core.map(v => v.legacy_viewing_id).filter(Boolean))
  return [...viewings.filter(v => !superseded.has(v.id)), ...core]
}

// ─── Manager team override (owner ruling, 2026-08-29) ─────────────────────────
// A manager earns a cut of every deal closed by someone IN THEIR OFFICE. Owner's words:
// "their commission is from the deal they close and manager's income is in addition on that
// when their team members close a deal … from that total commission". Three consequences
// this file has to hold, because none of them are obvious from the number alone:
//
//   1. THE BASE IS THE WHOLE DEAL COMMISSION, not the closing agent's share. So it is NOT
//      routed through agentDealIncome's split — that function divides one pot between the
//      ATTENDING agents, and a manager is not an attendee.
//   2. IT DOES NOT REDUCE ANYONE. The closing agent's income is byte-identical to what it
//      was before this feature; the agency's remainder is what absorbs the override.
//   3. NEVER ON THEIR OWN DEALS (owner's explicit choice). A manager who attended takes
//      their personal rate through the normal split and nothing else — one person, one
//      share, per deal.
//
// "Team" = the office. There is no manager_id and no team table in this schema — office_id
// is the only real grouping, and managers are already hard-scoped to their own office in
// App.jsx. Do not invent a second notion of team without moving that one too.
//
// ⚠️ Keyed on commission_manager BEING SET, not on role === 'Manager'. Roles are free text
// that admin can rename in config.agent_roles, so a rename must not silently stop paying
// people. The counterpart of that choice lives in AgentModal.save: it writes NULL the moment
// an agent stops being a Manager, so a stale number can never keep earning.
// ⚠️ THIS FUNCTION READS CURRENT STATE, AND THAT IS ONLY CORRECT AT THE MOMENT OF CLOSE.
// Office, rate and is_active are all taken off today's roster row. When a deal closes NOW that
// is the as-of answer and the stamp is right. It is wrong for a BACKDATED close (this form can
// create a deal already closed, and doOutcome can close one whose date is not today) and wrong
// for any re-stamp: after the 2026-08-19 reorg every historical Delisi deal re-stamped here
// would name Luka, who was a Senior Agent for the whole period the deals belong to.
// `opts.date` + `opts.history` (from getOfficeHistory) make it resolve as-of instead, using the
// same dated tables the ledger reads. Both optional: omitted, behaviour is byte-identical to
// before 2026-09-20, so no existing caller changes.
// ⚠️ The two callers — DealsTab and MeetingModal — do NOT pass them yet; they would each need
// to load the office history, and adding a fetch to a close path is its own decision. Until
// they do, a backdated close still stamps today's manager at today's rate.
export function managerLinksForDeal(agentIds = [], agents = [], opts = {}) {
  const { date = null, history = null } = opts
  const dated = !!(date && history)
  const officeOf = a => dated ? (officeAt(a.id, date, history.memberships) ?? a.office_id) : a.office_id
  const attending = new Set(agentIds)
  const offices = new Set(
    agents.filter(a => attending.has(a.id)).map(officeOf).filter(Boolean))
  if (!offices.size) return []
  return agents
    .map(a => {
      // A manager who has since LEFT still covered the office on the day the deal closed, and
      // is owed for it — which is why is_active is consulted only on the undated path.
      const covered = dated
        ? officesCoveredAt(a.id, date, history.coverage)
        : (a.office_id && a.is_active !== false ? [a.office_id] : [])
      const mine = [...offices].filter(o => covered.includes(o))
      const rate = dated
        ? (rateAt(a.id, 'manager', date, history.rates || []) ?? Number(a.commission_manager))
        : Number(a.commission_manager)
      return { a, mine, rate }
    })
    .filter(({ a, mine, rate }) => !attending.has(a.id) && mine.length && Number(rate) > 0)
    .map(({ a, mine, rate }) => ({ agent_id: a.id,
      ...(offices.size > 1 ? { office_id: mine[0], office_share_percent: 100 / offices.size } : {}),
      commission_pct: Number(rate) }))
}

// One manager's override income from a deal. links = viewings.manager_links (the SNAPSHOT
// taken at close). Returns 0 when this deal predates the feature or owed them nothing —
// deliberately no fallback HERE. (Since 2026-09-06 the LEDGER derives a missing override from the
// dated office-manager coverage before calling this — see statsModel.derivedManagerLinks — so an
// unstamped deal is paid there, not by this function guessing a rate.)
// No fallback to their current rate, because unlike commission_pct (which was
// always conceptually owed and merely unrecorded before snapshots existed) an override on a
// deal closed before the rule existed was never actually paid. Inventing it would rewrite
// history, which is the whole thing manager_links is here to prevent.
export function managerDealIncome(commissionAmount, managerLinks, agentId) {
  const link = (managerLinks || []).find(l => l.agent_id === agentId)
  if (!link) return 0
  const officeShare = Number(link.office_share_percent)
  const base = (Number(commissionAmount) || 0) * (Number.isFinite(officeShare) && officeShare > 0 ? officeShare / 100 : 1)
  return Math.round(base * (Number(link.commission_pct) || 0) / 100)
}

// Results (income) for one agent from the Deals/Viewings pipeline. Counts only
// status === 'closed' deals in [from, to] (by close date). totalCommission is the
// agent's INCOME in GEL — their cut from the snapshotted commission % they had at
// close (agent_links[].commission_pct), falling back to their current rate for
// deals closed before snapshots existed. Each deal's money is converted from its
// own commission_currency using the admin-set rates (fx = config.fx_to_gel;
// commissions are GEL or USD, occasionally EUR — 2026-07-12, replacing the old
// sum-raw-as-GEL behavior).
// managerCommission is kept SEPARATE from totalCommission on purpose. totalCommission is
// the ranking input (commissionPerDay → the leaderboard score); folding an office-wide
// override into it would rank a manager above the people who actually closed the deals,
// which nobody asked for. The Stats KPI adds the two for display — money earned is money
// earned — but the score keeps measuring personal work.
export function computeResultsFromViewings(viewings, agent, from, to, fx) {
  let count = 0, totalCommission = 0, totalValue = 0
  let managerCommission = 0, managerCount = 0
  // Solo vs co-brokered (owner ask, 2026-08-29: "did he close alone or with cooperation").
  // The test is the number of ATTENDING agents on the deal, which is exactly what drives the
  // split in agentDealIncome — so "cooperation" here means the same thing the payout means,
  // not a looser sense of the word. partners is keyed by the other agent's id; the caller
  // resolves names, because this module knows nothing about the roster.
  let soloCount = 0, soloCommission = 0, coopCount = 0, coopCommission = 0
  const partners = new Map()
  // Every deal this agent drew a manager override from, itemised (owner ask, 2026-08-29:
  // "for managers we need additional their split from each deal … detailed"). A single
  // total is unauditable — a manager cannot tell a missing deal from a low rate — so each
  // row carries the deal, who closed it, the rate applied and the money it produced.
  const managerDeals = []
  let bestDeal = { commission: 0, date: null }
  for (const v of viewings) {
    if (v.status !== 'closed') continue
    const links = v.agent_links || (v.agent_ids || []).map(id => ({ agent_id: id, commission_pct: null }))
    const mine = links.find(l => l.agent_id === agent.id)
    const date = viewingCloseDate(v)
    if (!mine) {
      // Not an attendee — but they may still be owed a team override on this deal.
      // Range-filtered with the same window as a normal deal.
      if (from && date < from) continue
      if (to && date > to) continue
      const dealComm = toGel(v.commission_amount, v.commission_currency, fx)
      const mgr = managerDealIncome(dealComm, v.manager_links, agent.id)
      if (mgr) {
        managerCommission += mgr
        managerCount++
        managerDeals.push({
          viewing_id: v.id,
          date,
          // The rate that was FROZEN onto this deal, not agents.commission_manager today.
          // Showing the live rate beside a historical amount is how a correct payout gets
          // reported as a bug.
          pct: Number((v.manager_links || []).find(l => l.agent_id === agent.id)?.commission_pct) || 0,
          dealCommission: Math.round(dealComm),
          income: mgr,
          closers: links.map(l => l.agent_id),
          address: v.property_snapshot?.address || v.cadastral_code || null,
        })
      }
      continue
    }
    if (from && date < from) continue
    if (to && date > to) continue
    // Fill a missing self-rate from the agent's current rate (pre-snapshot deals).
    const filled = links.map(l => ({
      agent_id: l.agent_id,
      commission_pct: l.commission_pct != null ? l.commission_pct : (l.agent_id === agent.id ? (agent.commission ?? 0) : 0),
    }))
    const income = agentDealIncome(toGel(v.commission_amount, v.commission_currency, fx), filled, agent.id, v.lead_agent_id)
    const val = (Number(v.price) || 0) / (links.length || 1)
    count++
    totalCommission += income
    totalValue += val
    if (links.length <= 1) { soloCount++; soloCommission += income }
    else {
      coopCount++; coopCommission += income
      for (const l of links) {
        if (l.agent_id === agent.id) continue
        const p = partners.get(l.agent_id) || { agent_id: l.agent_id, count: 0, commission: 0 }
        p.count++; p.commission += income   // income = MINE on that deal, not the partner's
        partners.set(l.agent_id, p)
      }
    }
    if (income > bestDeal.commission) bestDeal = { commission: income, date }
  }
  return {
    count, totalCommission: Math.round(totalCommission), totalValue: Math.round(totalValue), bestDeal,
    managerCommission: Math.round(managerCommission), managerCount,
    managerDeals: managerDeals.sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    soloCount, soloCommission: Math.round(soloCommission),
    coopCount, coopCommission: Math.round(coopCommission),
    partners: [...partners.values()]
      .map(p => ({ ...p, commission: Math.round(p.commission) }))
      .sort((a, b) => b.count - a.count || b.commission - a.commission),
  }
}

// ─── Composite performance score (Build #2) ───────────────────────────────────
// Each pillar → a 0–100 sub-score; the overall score is the weight-normalised
// blend of the sub-scores that have data. Weights are coefficients (any scale —
// only their ratio matters, since we divide by the sum of present weights), so a
// missing pillar (e.g. results, not built yet) simply drops out without dragging
// everyone down. Defaults live here; admin can override via config.
export const DEFAULT_WEIGHTS = { attendance: 40, activity: 60, results: 0 }

// Attendance pillar: attendance rate is the spine, on-time nudges it. Both are
// already 0–100. null (no scheduled days in range) → no attendance sub-score.
export function attendanceScore(stats) {
  if (stats.attendanceRate == null) return null
  if (stats.onTimeRate == null) return stats.attendanceRate
  return Math.round(0.7 * stats.attendanceRate + 0.3 * stats.onTimeRate)
}

// Generic "relative to team best" 0–100 sub-score. value null (no scheduled days)
// → null; cohortMax 0 (NOBODY in the team had any signal this range) → null so the
// pillar drops out of the blend rather than scoring everyone 0. A non-top agent
// with value 0 while others have signal correctly scores 0.
function relativeScore(value, cohortMax) {
  if (value == null || !cohortMax) return null
  return Math.round(Math.min(100, (value / cohortMax) * 100))
}

// Activity pillar: posts-per-scheduled-day scaled relative to the team best.
export function activityScore(perDay, cohortMaxPerDay) {
  return relativeScore(perDay, cohortMaxPerDay)
}

// Results pillar: commission-per-scheduled-day scaled relative to the team best.
export function resultsScore(commissionPerDay, cohortMaxPerDay) {
  return relativeScore(commissionPerDay, cohortMaxPerDay)
}

// Weighted blend of the present sub-scores. subs: { attendance, activity, results }
// (0–100 or null). Returns 0–100, or null if no pillar has both data and weight.
export function compositeScore(subs, weights = DEFAULT_WEIGHTS) {
  let num = 0, den = 0
  for (const k of ['attendance', 'activity', 'results']) {
    const s = subs[k]
    const w = weights?.[k] ?? 0
    if (s == null || w <= 0) continue
    num += w * s
    den += w
  }
  return den > 0 ? Math.round(num / den) : null
}
